import * as React from 'react';

import {
  enumBrokeredOrderStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, ContentCard, Footer } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import { BrokeredOrderLineItemsTable } from '../_shared/LineItems';
import { BrokeredOrderLineItemsSummaryFooter } from '../_shared/LineItems/BrokeredOrderLineItemsSummaryFooter';

import {
  BrokeredOrderLineItemUpdateInput,
  brokeredOrderUpdateAtom,
} from '~/modules/brokerage/_graphql/mutationBrokeredOrderUpdate';
import {
  $brokeredOrderDraftLineItems,
  $brokeredOrderEffectiveLineItems,
  $brokeredOrderValidation,
} from '~/modules/brokerage/_state/brokeredOrderDetail.state';
import { $brokeredOrderMarginsDataByParams } from '~/modules/brokerage/_state/brokeredOrderMargins.state';
import { useBrokeredOrderLineItemsManager } from '~/modules/brokerage/hooks/useBrokeredOrderLineItemsManager';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { BrokeredOrderDetailRecord } from '~/modules/brokerage/types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '~/modules/brokerage/types/brokeredOrderLineItem';

type Props = {
  order?: BrokeredOrderDetailRecord | null;
  onRefetch?: () => void;
};

const TEMP_ID_PREFIX = 'temp-';

export const BrokeredOrderTabMaterials: React.FC<Props> = ({
  order,
  onRefetch,
}) => {
  const t = useTranslations();
  const toast = useToast();

  const [isSaving, setIsSaving] = React.useState(false);

  const { data: validationResult } = useAtomValue($brokeredOrderValidation);
  const effectiveLineItems = useAtomValue($brokeredOrderEffectiveLineItems);
  const setDraftLineItems = useSetAtom($brokeredOrderDraftLineItems);
  const updateOrder = useSetAtom(brokeredOrderUpdateAtom({}));

  React.useEffect(() => {
    if (order?.timestamps?.updatedAt) {
      setDraftLineItems(null);
    }
  }, [order?.timestamps?.updatedAt, setDraftLineItems]);

  const canEdit =
    order?.status === enumBrokeredOrderStatus.Draft ||
    order?.status === enumBrokeredOrderStatus.Open;

  const purchaseOrderIds = React.useMemo(
    () => order?.purchaseOrders.map((po) => po.id) ?? [],
    [order?.purchaseOrders],
  );
  const salesOrderIds = React.useMemo(
    () => order?.salesOrders.map((so) => so.id) ?? [],
    [order?.salesOrders],
  );

  const marginsAtom = React.useMemo(
    () =>
      $brokeredOrderMarginsDataByParams({ purchaseOrderIds, salesOrderIds }),
    [purchaseOrderIds, salesOrderIds],
  );
  const marginsData = useAtomValue(marginsAtom);

  const handleLineItemsChange = React.useCallback(
    (items: BrokeredOrderLineItemRecord[]) => setDraftLineItems(items),
    [setDraftLineItems],
  );

  const {
    addLineItem,
    addLineItemsFromOrders,
    updateLineItem,
    deleteLineItem,
    validationErrorsByItemId,
    hasUnaddedOrderItems,
    hasLinkedOrders,
  } = useBrokeredOrderLineItemsManager({
    lineItems: effectiveLineItems,
    onLineItemsChange: handleLineItemsChange,
    marginsData,
    serverValidationErrors: validationResult?.errors,
  });

  const handleDiscard = React.useCallback(() => {
    setDraftLineItems(null);
    toast.success(t.orders.toast.discarded);
  }, [setDraftLineItems, toast, t]);

  const handleSave = React.useCallback(async () => {
    if (!order?.id) return;

    const validItems = effectiveLineItems.filter((li) => li.itemId);

    if (validItems.length === 0 && effectiveLineItems.length > 0) {
      toast.error(t.orders.toast.materialRequired);
      return;
    }

    try {
      setIsSaving(true);

      const lineItemsInput: BrokeredOrderLineItemUpdateInput[] = validItems.map(
        (li) => ({
          id: li.id.startsWith(TEMP_ID_PREFIX) ? undefined : li.id,
          itemId: li.itemId,
          quantity: li.quantity,
          unit: li.unit as Unit,
          notes: li.notes,
        }),
      );

      await updateOrder({
        id: order.id,
        lineItems: lineItemsInput,
      });

      toast.success(t.orders.toast.updated);
      onRefetch?.();
    } catch (err) {
      toast.error(
        t.orders.toast.updateError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [order?.id, effectiveLineItems, updateOrder, toast, t, onRefetch]);

  if (!order) return null;

  return (
    <div
      className={cn(
        '@BrokeredOrderTabMaterials',
        'flex h-full flex-col overflow-hidden p-5',
      )}
    >
      <ContentCard className="flex flex-col overflow-hidden rounded-lg p-0">
        <div className="flex-1 overflow-auto px-5 py-6 pr-4">
          <BrokeredOrderLineItemsTable
            lineItems={effectiveLineItems}
            disabled={!canEdit}
            validationErrorsByItemId={validationErrorsByItemId}
            validationRan={hasLinkedOrders || !!validationResult}
            onUpdateLineItem={updateLineItem}
            onDeleteLineItem={deleteLineItem}
            onAddLineItem={addLineItem}
          />
          {canEdit && hasUnaddedOrderItems && (
            <div className="mt-4 flex justify-start">
              <Button
                variant="outline"
                color="neutral"
                onPress={addLineItemsFromOrders}
                icon={<LucidePlusCircle />}
              >
                {t.orders.materials.buttons.addFromOrders}
              </Button>
            </div>
          )}
        </div>

        <Footer
          className="@BrokeredOrderMaterialsFooter flex-shrink-0"
          displayShadow
        >
          <div className="flex items-center justify-end gap-5 px-5 py-2.5">
            <BrokeredOrderLineItemsSummaryFooter
              lineItems={effectiveLineItems}
              marginsData={marginsData}
            />

            <div className="ml-4 flex items-center justify-end gap-5">
              <Button
                color="neutral"
                variant="outline"
                onPress={handleDiscard}
                isDisabled={isSaving}
              >
                {t.orders.detail.footer.discard}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isSaving}
                isLoading={isSaving}
              >
                {t.orders.detail.footer.saveUpdates}
              </Button>
            </div>
          </div>
        </Footer>
      </ContentCard>
    </div>
  );
};
